/**
 * TaskCreationButton Component
 *
 * Button to create a self-initiated "comment on photo" task.
 * Shows different states: default (clickable), pending (disabled), loading (spinner).
 *
 * Feature: 648-task-triggers (User Story 2)
 */

import { cn } from '@/lib/utils';
import {
  useHasPendingTaskForPhotoInCritique,
  useCreateTaskGraphQL,
} from '@/graphql/hooks';
import logger from '@/utils/logger';

export interface TaskCreationButtonProps {
  /** Global ID of the photo to create task for */
  photoId: string;
  /** Global ID of the critique session (required for photo_in_critique target) */
  sessionId: string;
  /** Additional CSS classes */
  className?: string;
  /** Called when task is created or already exists */
  onTaskCreated?: (taskId: string, created: boolean) => void;
}

/**
 * Task icon (clipboard with checkmark)
 */
function TaskIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Clipboard outline */}
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      {/* Clipboard tab */}
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      {/* Plus sign for "add task" */}
      <line x1="12" y1="11" x2="12" y2="17" />
      <line x1="9" y1="14" x2="15" y2="14" />
    </svg>
  );
}

/**
 * Check icon for pending state
 */
function CheckIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Clipboard outline */}
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      {/* Clipboard tab */}
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      {/* Checkmark */}
      <polyline points="9 13 11 15 15 11" />
    </svg>
  );
}

/**
 * Loading spinner
 */
function LoadingSpinner({ size = 16 }: { size?: number }) {
  return (
    <div
      className="animate-spin rounded-full border-2 border-current border-t-transparent"
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}

/**
 * TaskCreationButton - Creates a "comment on photo" task
 *
 * States:
 * - Default: Clickable button with task icon
 * - Pending: Disabled button with check icon and "Task pending" tooltip
 * - Loading: Button with spinner while creating task
 *
 * @example
 * ```tsx
 * <TaskCreationButton
 *   photoId={photo.globalId}
 *   onTaskCreated={(taskId, created) => {
 *     if (created) toast.success('Task created!');
 *   }}
 * />
 * ```
 */
export function TaskCreationButton({
  photoId,
  sessionId,
  className,
  onTaskCreated,
}: TaskCreationButtonProps) {
  const { hasPendingTask, loading: checkingPending } = useHasPendingTaskForPhotoInCritique(photoId, sessionId);
  const { createTask, loading: creating } = useCreateTaskGraphQL();

  const isLoading = checkingPending || creating;
  const isDisabled = hasPendingTask || isLoading;

  const handleClick = async () => {
    if (isDisabled) return;

    try {
      // Use photo_in_critique target type:
      // - targetId = sessionId (the critique session)
      // - targetContext.photoId = photoId (the specific photo in that session)
      const { task, created } = await createTask({
        actionType: 'COMMENT_ON_PHOTO',
        targetType: 'photo_in_critique',
        targetId: sessionId,
        targetContext: { photoId },
      });

      logger.info('Tasks', created ? 'Task created via button' : 'Task already pending', {
        taskId: task.id,
        photoId,
        sessionId,
      });

      onTaskCreated?.(task.id, created);
    } catch (error) {
      logger.error('Tasks', 'Failed to create task', {
        photoId,
        sessionId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  // Determine button state and appearance
  const getStateInfo = () => {
    if (isLoading && !hasPendingTask) {
      return {
        icon: <LoadingSpinner size={16} />,
        title: 'Creating task...',
        ariaLabel: 'Creating task',
      };
    }
    if (hasPendingTask) {
      return {
        icon: <CheckIcon size={16} className="text-green-500" />,
        title: 'Task pending - you have a task to comment on this photo',
        ariaLabel: 'Task pending',
      };
    }
    return {
      icon: <TaskIcon size={16} />,
      title: 'Create task to comment on this photo',
      ariaLabel: 'Create comment task',
    };
  };

  const { icon, title, ariaLabel } = getStateInfo();

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={cn(
        'inline-flex items-center justify-center',
        'w-7 h-7 rounded',
        'text-muted-foreground hover:text-foreground',
        'hover:bg-surface-2 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        className
      )}
      title={title}
      aria-label={ariaLabel}
    >
      {icon}
    </button>
  );
}
